import { useDynamicLocale } from '@/hoslog-intl';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import { Select as Root, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'ui/select';

interface SelectOption {
  label: string;
  value: string;
  labelEng?: string;
}

interface Props extends Omit<React.ComponentProps<typeof Root>, 'value' | 'onValueChange'> {
  name: string;
  options: SelectOption[];
  placeholder?: string;
  value?: string;
  onValueChange?: (value: string) => void | Promise<unknown>;
  selected?: string;
  triggerLabel?: string;
  labelInTrigger?: boolean;
  triggerClassName?: string;
  valueClassName?: string;
  itemClassName?: string;
  contentClassName?: string;
}

function Select(props: Props) {
  const { locale, t } = useDynamicLocale();
  const {
    options,
    name,
    placeholder,
    value,
    onValueChange,
    selected,
    triggerLabel,
    labelInTrigger = false,
    triggerClassName,
    valueClassName,
    itemClassName,
    contentClassName,
  } = props;

  const currentValue = (value as string) || selected || 'default';
  const selectedOption = options.find((option) => option.value === currentValue);

  const getLabel = (option: SelectOption) => (locale === 'en' && option.labelEng ? option.labelEng : option.label);

  return (
    <Root
      onValueChange={(val) => {
        void onValueChange?.(val);
      }}
      value={currentValue}
    >
      <SelectTrigger id={name} className={triggerClassName} icon={<ChevronDown className="size-5" />}>
        <SelectValue placeholder={placeholder ?? t('common.form.placeholder.select')}>
          {labelInTrigger && selectedOption ? (
            <span className={cn('truncate', valueClassName)}>
              {triggerLabel ? <span>{triggerLabel}: </span> : null}
              {getLabel(selectedOption)}
            </span>
          ) : selectedOption ? (
            <span className={valueClassName}>{getLabel(selectedOption)}</span>
          ) : undefined}
        </SelectValue>
      </SelectTrigger>

      <SelectContent className={contentClassName}>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} className={itemClassName}>
            {getLabel(option)}
          </SelectItem>
        ))}
      </SelectContent>
    </Root>
  );
}

Select.displayName = 'Select';
export default Select;
